import { oc } from 'ts-optchain';

import { core, safeEnumCompare } from '@tock/coretypes';
import {
  getTierForPartySize,
  hasCreditCardHoldPriceConfig,
} from '@tock/shared/utils/ticketTypeUtils';
import { isTotallyFreePurchase } from 'utils/purchaseOrderUtils';

import { Priceable } from 'components/business/checkout/checkoutHooks';
import { isTogoVariety } from '@tock/shared/utils/offerings';
import protos from 'utils/protos';

const { TicketType } = protos;

export function isQuickCheckoutEligible(
  currentLock?: core.TicketGroup,
  isFeatureEnabled?: (feature: core.Feature.FeatureName) => boolean
) {
  if (!currentLock) {
    return false;
  }

  const { ticketOptionGroup, ticketOption } = currentLock;
  const noNonTockImposedFees = currentLock.fee.every((fee) => fee.isTockImposed);
  const ticketPriceCents = oc(currentLock.type[0]).amount() || 0;

  const isTicketTypeReservation = safeEnumCompare(
    currentLock.ticketTypeVariety,
    TicketType.Variety,
    TicketType.Variety.RESERVATION
  );

  let addonsTotalPriceCents = 0;
  if (ticketOptionGroup.length && ticketOption.length) {
    addonsTotalPriceCents += calcTicketOptionGroupCents(ticketOptionGroup);
    addonsTotalPriceCents += calcTicketOptionCents(ticketOption);
  }

  return isQuickCheckoutEligibleInternal(
    {
      addonsTotalPriceCents,
      hasNonTockImposedFees: !noNonTockImposedFees,
      ticketPriceCents,
      isEffectivelyFree: isTicketTypeReservation,
      isTockToGo: isTogoVariety(currentLock.ticketTypeVariety),
    },
    isFeatureEnabled
  );
}

export function isQuickCheckoutEligibleExperience(
  experience: core.ITicketType,
  priceCents: number,
  isFeatureEnabled?: (feature: core.Feature.FeatureName) => boolean,
  partySize?: number
) {
  const addonsTotalPriceCents = experience.optionGroup
    ? calcTicketOptionGroupCents(experience.optionGroup)
    : 0;
  const hasNonTockImposedFees = !experience?.fee?.every((fee) => fee.isTockImposed);
  const isTypeReservation = experience?.variety === 'RESERVATION';
  let isFreeTierCCHold = false;

  if (partySize && hasCreditCardHoldPriceConfig(experience)) {
    const matchingTier = getTierForPartySize(
      experience.ticketTypePriceConfig as core.ITicketTypePriceConfig[],
      partySize
    );
    if (matchingTier?.priceCents === 0) {
      isFreeTierCCHold = true;
    }
  }

  return isQuickCheckoutEligibleInternal(
    {
      addonsTotalPriceCents,
      hasNonTockImposedFees,
      ticketPriceCents: priceCents,
      isEffectivelyFree: isTypeReservation || isFreeTierCCHold,
      isTockToGo: isTogoVariety(experience?.variety),
    },
    isFeatureEnabled
  );
}

export function isQuickCheckoutEligibleInternal(
  {
    addonsTotalPriceCents,
    hasNonTockImposedFees,
    ticketPriceCents,
    isEffectivelyFree,
    isTockToGo,
  }: {
    addonsTotalPriceCents: number;
    hasNonTockImposedFees: boolean;
    ticketPriceCents: number;
    isEffectivelyFree: boolean;
    isTockToGo: boolean;
  },
  isFeatureEnabled?: (feature: core.Feature.FeatureName) => boolean
) {
  // There aren't current plans to use PQC with Tock To Go offerings
  const isPaidQuickCheckoutEnabled =
    (isFeatureEnabled?.('PAID_QUICK_CHECKOUT') && !isTockToGo) ?? false;

  // We want users in UR creating a Tock account, so we explicitly disable the quick checkout.
  if (isFeatureEnabled?.('CONSUMER_VAIL_RESTRICT_B2B_RESERVATIONS')) {
    return false;
  }

  const isFreeReservation: boolean =
    isEffectivelyFree &&
    ticketPriceCents <= 0 &&
    addonsTotalPriceCents <= 0 &&
    !hasNonTockImposedFees;

  return isFreeReservation || isPaidQuickCheckoutEnabled;
}

// @VisibleForTesting
export function calcTicketOptionGroupCents(optGroup: core.ITicketOptionGroup[]) {
  let totalCents = 0;

  optGroup.forEach((grp) => {
    if (grp.ticketOption) {
      totalCents += calcTicketOptionCents(grp.ticketOption);
    }
  });

  return totalCents;
}

// @VisibleForTesting
export function calcTicketOptionCents(arr: core.ITicketOption[]) {
  return arr.reduce((tot, { defaultPrice }) => tot + (defaultPrice ?? 0), 0);
}

/**
 * Paid Quick Checkout is enabled when both the feature flag is toggled and a user is not logged in.
 * @returns: Boolean of whether Paid Quick Checkout is applicable.
 */
export function shouldUsePaidQuickCheckout(
  price: Priceable,
  jwtToken: String,
  isPaidQuickCheckoutEnabled: boolean
) {
  const isAuthenticated = !!jwtToken;
  const isTotallyFreeReservation = isTotallyFreePurchase(price);
  return !isAuthenticated && isPaidQuickCheckoutEnabled && !isTotallyFreeReservation;
}
